import React from 'react';
import {useTranslation} from 'react-i18next';

import type {ExtendedTooltipPosition, ModalProps} from '@pexip/components';
import {
    Button,
    Modal,
    ModalCloseButton as CloseButton,
    NotificationTooltip,
} from '@pexip/components';

import {TOOLTIP_Z_INDEX_BELOW_OVERLAYS} from '../../constants';
import {getUserAgentDetails} from '../../utils/userAgentDetails';

import styles from './InputControlTooltip.module.scss';

const ToolTipModal: React.FC<
    {
        ariaLabel?: string;
        content: React.ReactNode;
        children: React.ReactNode;
        visible: boolean;
        onClose?: () => void;
    } & Omit<ModalProps, 'isOpen' | 'content'>
> = ({ariaLabel, content, children, onClose, visible, ...props}) => {
    const {t} = useTranslation();

    return (
        <>
            <Modal
                isOpen={visible}
                closeOnOutsideClick={true}
                withCloseButton={false}
                onClose={onClose}
                uniqueTitle={ariaLabel}
                scrollbarsAriaLabel={t(
                    'common.scrollable-content',
                    'Scrollable content',
                )}
                {...props}
            >
                {content}
                <Button
                    size="large"
                    className="mt-3"
                    modifier="fullWidth"
                    onClick={onClose}
                >
                    {t('common.ok', 'Ok')}
                </Button>
            </Modal>
            {children}
        </>
    );
};

const ToolTipBalloon: React.FC<{
    ariaLabel?: string;
    content: React.ReactNode;
    children: React.ReactNode;
    visible: boolean;
    mainWrapperClassName?: string;
    tooltipClassName?: string;
    wrapperClassName?: string;
    position: ExtendedTooltipPosition;
    testid?: string;
}> = ({
    ariaLabel,
    content,
    children,
    visible,
    tooltipClassName,
    wrapperClassName,
    position,
    testid,
    ...props
}) => {
    return (
        <NotificationTooltip
            zIndex={TOOLTIP_Z_INDEX_BELOW_OVERLAYS}
            ariaLabel={ariaLabel}
            className={tooltipClassName}
            header={undefined}
            text={''}
            controlledIsVisible={visible}
            ctaText={<div className="p-0">{content}</div>}
            position={position}
            testIdInner={testid}
            {...props}
        >
            {children}
        </NotificationTooltip>
    );
};

export const InputControlTooltip: React.FC<{
    ariaLabel?: string;
    isTouch?: boolean;
    children: React.ReactNode;
    content: React.ReactNode;
    contentWrapperClassName?: string;
    mainWrapperClassName?: string;
    position?: ExtendedTooltipPosition;
    onClose?: () => void;
    tooltipClassName?: string;
    visible: boolean;
    testid?: string;
}> = ({
    ariaLabel,
    isTouch = getUserAgentDetails(navigator.userAgent).isTouch,
    children,
    content,
    contentWrapperClassName,
    mainWrapperClassName,
    position = 'topCenter',
    onClose,
    tooltipClassName,
    visible,
    testid,
    ...props
}) => {
    return isTouch ? (
        <ToolTipModal
            ariaLabel={ariaLabel}
            content={content}
            visible={visible}
            onClose={onClose}
            {...props}
        >
            {children}
        </ToolTipModal>
    ) : (
        <ToolTipBalloon
            ariaLabel={ariaLabel}
            visible={visible}
            position={position}
            content={content}
            tooltipClassName={tooltipClassName}
            testid={testid}
            {...props}
        >
            {children}
        </ToolTipBalloon>
    );
};

export const InputControlTooltipClose: React.FC<
    React.ComponentProps<typeof CloseButton>
> = props => {
    const {t} = useTranslation();
    return (
        <CloseButton
            className={styles.close}
            aria-label={t('common.close-tooltip', 'Close tooltip')}
            {...props}
        />
    );
};
